/* ============================================================================
   ServiceSetup — the one-stop configuration hub for everything that powers
   the case-creation wizard.

   iter101.32 — Consolidates two previously-scattered admin surfaces:
      • /admin/finance?tab=pricing → Treatment Tiers · Promotions · Priority
        Tiers · Wizard Cards · Wizard Copy (moved out of Finance because
        this is service config, not money movement)
      • /admin/plan-authoring       → Plan Template sections · fields ·
        clinical thresholds (moved out of Admin group because this defines
        what the doctor authors during case creation)

   iter101.33 — Mockup retrofit:
      • 4-tile canonical StatTile strip at the top of the hub
        (Active tiers · Promotions · Priority tiers · Add-on services)
        with click-through routing into the matching Pricing sub-tab.
      • Sub-tab (?sub=) URL param owned here + passed to Pricing so tiles
        can deep-link into the right section.
   ========================================================================== */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";
import { Tag, ClipboardText, ListBullets, Lightning, Sparkle } from "@phosphor-icons/react";
import { StatTile } from "@/pages/admin/Finance";

/* Reuse the fully-built sub-apps. Both already contain their own
   internal AdminSegmentedTabs for their sub-sections (Tiers/Promos/...
   in Pricing, Sections/Fields/Thresholds in PlanAuthoring). */
import Pricing from "./Pricing";
import PlanAuthoring from "./AdminPlanAuthoring";

/* Fixed count — 2 add-on TYPES currently available for admin to configure
   (IO Scans by Invisique™ + Compliance Monitoring & Support). Bumps up if
   a third add-on ever ships. */
const ADDON_TYPE_COUNT = 2;

export default function ServiceSetup() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") || "pricing";
    const sub = searchParams.get("sub") || "tiers";

    const setTab = (id) => {
        const next = new URLSearchParams(searchParams);
        if (id === "pricing") next.delete("tab");
        else next.set("tab", id);
        // sub is only meaningful for the Pricing tab, drop it on tab switch
        next.delete("sub");
        setSearchParams(next, { replace: true });
    };

    const setSub = (id) => {
        const next = new URLSearchParams(searchParams);
        next.delete("tab"); // pricing is the default tab
        if (id === "tiers") next.delete("sub");
        else next.set("sub", id);
        setSearchParams(next, { replace: true });
    };

    const [counts, setCounts] = useState({ tiers: 0, promos: 0, priorities: 0 });

    useEffect(() => {
        Promise.all([
            api.get("/admin/tiers"),
            api.get("/admin/promotions"),
            api.get("/admin/priority-tiers"),
        ])
            .then(([t, p, pr]) => {
                setCounts({
                    tiers: (t.data || []).filter((x) => x.is_active).length,
                    promos: (p.data || []).filter((x) => x.is_active).length,
                    priorities: (pr.data || []).filter((x) => x.is_active).length,
                });
            })
            .catch(() => { /* soft-fail — tiles show 0 */ });
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-service-setup-page">
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Operations · Service Setup</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Service Setup</h1>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Configure tiers, wizard copy, and plan templates — the shape of every case a doctor creates.
                </p>
            </div>

            {/* iter101.33 — Canonical 4-tile KPI strip. Each tile deep-links into
                the matching Pricing sub-tab. Add-ons count is intentionally
                fixed at the number of add-on TYPES available (not activations). */}
            <div
                className="grid grid-cols-2 md:grid-cols-4 gap-3"
                data-testid="service-setup-kpi-strip"
            >
                <TileButton onClick={() => setSub("tiers")} testid="service-setup-kpi-tiers">
                    <StatTile
                        label="Active tiers"
                        value={counts.tiers}
                        Icon={ListBullets}
                        tone="primary"
                    />
                </TileButton>
                <TileButton onClick={() => setSub("promos")} testid="service-setup-kpi-promos">
                    <StatTile
                        label="Promotions"
                        value={counts.promos}
                        Icon={Tag}
                        tone="emerald"
                    />
                </TileButton>
                <TileButton onClick={() => setSub("priorities")} testid="service-setup-kpi-priorities">
                    <StatTile
                        label="Priority tiers"
                        value={counts.priorities}
                        Icon={Lightning}
                        tone="amber"
                    />
                </TileButton>
                <TileButton onClick={() => setSub("addons")} testid="service-setup-kpi-addons">
                    <StatTile
                        label="Add-on services"
                        value={ADDON_TYPE_COUNT}
                        Icon={Sparkle}
                        tone="violet"
                    />
                </TileButton>
            </div>

            <AdminSegmentedTabs
                testid="service-setup-tabs"
                tabTestidBase="service-setup-tab"
                active={tab}
                onChange={setTab}
                tabs={[
                    { id: "pricing",       label: "Pricing & Wizard",  Icon: Tag },
                    { id: "plan-templates", label: "Plan Templates",   Icon: ClipboardText },
                ]}
            />

            {tab === "pricing"        && <Pricing embedded subTab={sub} setSubTab={setSub} />}
            {tab === "plan-templates" && <PlanAuthoring embedded />}
        </div>
    );
}

/* Thin button wrapper so each KPI tile is a real, focusable, click-through
   affordance without polluting the canonical StatTile signature. */
function TileButton({ children, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className="text-left rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/40 hover:-translate-y-0.5 hover:shadow-sm transition-all"
        >
            {children}
        </button>
    );
}
